"use client"

import type React from "react"
import { useEffect } from "react"
import { useBlocker, useNavigate } from "react-router-dom"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { AlertTriangle } from "lucide-react"

const BrowserNotLeave: React.FC = () => {
  const navigate = useNavigate()

  // bloqueia apenas troca de rota interna
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      currentLocation.pathname !== nextLocation.pathname
  )

  // cobre refresh, fechar aba, back/forward direto do navegador
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (blocker.state === "blocked") {
        e.preventDefault()
        e.returnValue = ""
      }
    }
    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [blocker.state])

  // cleanup quando desmontar
  useEffect(() => {
    return () => {
      if (blocker && blocker.reset) {
        blocker.reset()
      }
    }
  }, [blocker])

  if (blocker.state !== "blocked") return null

  const nextLocation = blocker.location

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <Card className="w-full max-w-md mx-auto p-8 rounded-xl border border-gray-200 shadow-xl">
        <div className="flex flex-col items-center text-center space-y-6">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 border-2 border-amber-200">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>

          <div className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900 leading-tight">
              Alterações não salvas
            </h3>
            <p className="text-base text-gray-600 leading-relaxed">
              Ao sair desta página, todas as alterações não salvas serão perdidas permanentemente.
            </p>
            <p className="text-sm font-medium text-gray-800">
              Deseja continuar mesmo assim?
            </p>
          </div>

          <div className="flex gap-3 w-full pt-2">
            <Button
              variant="outline"
              onClick={() => blocker.reset()}
              className="flex-1 h-11 font-medium"
            >
              Voltar
            </Button>
            <Button
              variant="default"
              onClick={() => {
                blocker.proceed()
                if (nextLocation) {
                  // força navegação correta mesmo em back/forward
                  navigate(nextLocation.pathname + nextLocation.search + nextLocation.hash, {
                    replace: true,
                  })
                }
              }}
              className="flex-1 h-11 font-semibold"
            >
              Continuar
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default BrowserNotLeave
